export type DatasetQualityState = "ready" | "no_data" | "no_dataset" | "reconnect" | "unavailable";

export interface DatasetQualityMatchKey {
  key: string;
  label: string;
  coverage: number | null;
}

export interface DatasetQualityDiagnostic {
  name: string;
  description: string | null;
  solution: string | null;
  percentage: number | null;
  affected_event_count: number | null;
  total_event_count: number | null;
}

export interface DatasetQualityEvent {
  event_name: string;
  score: number | null;
  match_keys: DatasetQualityMatchKey[];
  diagnostics: DatasetQualityDiagnostic[];
  freshness: { frequency: string | null; label: string | null } | null;
  event_coverage: { percentage: number | null; goal_percentage: number | null; description: string | null } | null;
  acr: { percentage: number | null; description: string | null } | null;
  scored?: boolean;
  received_28d?: number | null;
  last_received_at?: string | null;
  used_by_ad_sets?: number | null;
}

export interface DatasetQualityHour {
  t: string;
  total: number;
  events: Record<string, number>;
}

export interface DatasetQualityDetails {
  name: string | null;
  created_at: string | null;
  last_received_at: string | null;
  automatic_matching: boolean | null;
  automatic_matching_fields: string[];
}

export interface DatasetQualityView {
  state: DatasetQualityState;
  reason?: string | null;
  ad_account_id: string | null;
  ad_account_name: string | null;
  dataset_id: string | null;
  dataset_source: "explicit" | "fallback" | "none";
  events: DatasetQualityEvent[];
  scored_count?: number;
  dataset?: DatasetQualityDetails | null;
  activity?: { window_days: number; hours: DatasetQualityHour[] } | null;
  parameters?: Array<{ name: string; count: number }> | null;
  ad_sets?: { total: number; active: number } | null;
  partial?: string[] | null;
  refresh?: DatasetQualityRefresh | null;
}

export type DatasetQualityRefreshStatus = "refreshed" | "cooldown" | "busy" | "rate_limited";

export interface DatasetQualityRefresh {
  status: DatasetQualityRefreshStatus;
  retry_after_seconds: number | null;
  available_at?: number | null;
}

const REFRESH_STATUSES: ReadonlySet<string> = new Set(["refreshed", "cooldown", "busy", "rate_limited"]);
const MAX_REFRESH_WAIT_SECONDS = 60 * 60;

export function stampRefreshAvailability(view: DatasetQualityView, now: number = Date.now()): DatasetQualityView {
  const refresh = view?.refresh;
  if (!refresh || !REFRESH_STATUSES.has(refresh.status)) return view;
  const seconds = Number(refresh.retry_after_seconds);
  const wait = Number.isFinite(seconds) && seconds > 0 ? Math.min(seconds, MAX_REFRESH_WAIT_SECONDS) : 0;
  return { ...view, refresh: { ...refresh, available_at: wait ? now + wait * 1000 : null } };
}

export function refreshWaitMs(view: DatasetQualityView | null | undefined, now: number = Date.now()): number {
  const at = Number(view?.refresh?.available_at);
  if (!Number.isFinite(at)) return 0;
  return Math.max(0, at - now);
}

export function formatWait(ms: number): string {
  const seconds = Math.max(1, Math.ceil(ms / 1000));
  if (seconds < 60) return `${seconds} ${seconds === 1 ? "second" : "seconds"}`;
  const minutes = Math.ceil(seconds / 60);
  return `${minutes} ${minutes === 1 ? "minute" : "minutes"}`;
}

export function refreshNotice(refresh: DatasetQualityRefresh | null | undefined): { title: string; description: string } | null {
  if (!refresh || refresh.status === "refreshed" || !REFRESH_STATUSES.has(refresh.status)) return null;
  const seconds = Number(refresh.retry_after_seconds);
  const wait = Number.isFinite(seconds) && seconds > 0 ? ` Try again in ${formatWait(seconds * 1000)}.` : "";
  if (refresh.status === "cooldown") {
    return { title: "Refreshed less than a minute ago", description: `Showing the data quality already loaded.${wait}` };
  }
  if (refresh.status === "busy") {
    return {
      title: "Close to Meta's request limit",
      description: `Showing the saved data quality so other Meta features keep working.${wait}`,
    };
  }
  return { title: "Meta is limiting requests", description: `Showing the saved data quality.${wait}` };
}

const KNOWN_STATES: ReadonlySet<string> = new Set(["ready", "no_data", "no_dataset", "reconnect", "unavailable"]);

export function datasetQualityState(view: DatasetQualityView | null | undefined): DatasetQualityState | null {
  if (!view || typeof view !== "object") return null;
  if (KNOWN_STATES.has(view.state)) {
    if (view.state === "ready" && !(Array.isArray(view.events) && view.events.length)) return "no_data";
    return view.state;
  }
  return null;
}

export function eventsManagerDatasetUrl(datasetId: string | null | undefined): string | null {
  const id = String(datasetId ?? "").trim();
  if (!/^\d{5,25}$/.test(id)) return null;
  return `https://business.facebook.com/events_manager2/list/dataset/${id}/overview`;
}

export function formatScore(score: number | null | undefined): string {
  return typeof score === "number" && Number.isFinite(score) ? score.toFixed(1) : "–";
}

export function formatPercent(value: number | null | undefined): string {
  if (typeof value !== "number" || !Number.isFinite(value)) return "–";
  return `${Number.isInteger(value) ? value : value.toFixed(1)}%`;
}

export function barWidth(value: number | null | undefined, max = 100): number {
  if (typeof value !== "number" || !Number.isFinite(value) || max <= 0) return 0;
  return Math.max(0, Math.min(100, (value / max) * 100));
}

export function weakestMatchKey(event: DatasetQualityEvent): DatasetQualityMatchKey | null {
  const keys = (event?.match_keys ?? []).filter((k) => typeof k.coverage === "number");
  if (!keys.length) return null;
  const weakest = keys.reduce((low, k) => ((k.coverage as number) < (low.coverage as number) ? k : low));
  return (weakest.coverage as number) < 100 ? weakest : null;
}

const LEAD_FORM_KEYS: ReadonlySet<string> = new Set(["email", "phone", "fn", "ln", "ct", "st", "zp", "country", "db", "ge"]);
const WEBSITE_KEYS: ReadonlySet<string> = new Set(["ip_address", "user_agent", "fbc", "fbp"]);

export function matchKeyTip(key: string | null | undefined): string {
  const k = String(key ?? "").toLowerCase();
  if (LEAD_FORM_KEYS.has(k)) return "Asking for it on your lead forms lets WeNext send it to Meta.";
  if (WEBSITE_KEYS.has(k)) return "Lead form submissions do not include this detail.";
  return "Meta recommends sending as many customer details as you can.";
}

export function isScoredEvent(event: DatasetQualityEvent): boolean {
  return typeof event.scored === "boolean" ? event.scored : typeof event.score === "number";
}

export function formatReceived(count: number | null | undefined): string | null {
  if (typeof count !== "number" || !Number.isFinite(count) || count < 0) return null;
  return `${count.toLocaleString()} received in 28d`;
}

function localDayKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export interface DatasetQualityDay {
  date: string;
  label: string;
  count: number;
}

export function dailyActivity(
  hours: DatasetQualityHour[] | null | undefined,
  { eventName = null, windowDays = 28, now = new Date() }: { eventName?: string | null; windowDays?: number; now?: Date } = {},
): DatasetQualityDay[] {
  const days = Math.max(1, Math.min(90, Math.floor(Number(windowDays) || 28)));
  const totals = new Map<string, number>();
  for (const hour of Array.isArray(hours) ? hours : []) {
    const at = new Date(hour?.t);
    if (Number.isNaN(at.getTime())) continue;
    const n = eventName ? Number(hour?.events?.[eventName] ?? 0) : Number(hour?.total ?? 0);
    if (!Number.isFinite(n) || n <= 0) continue;
    const key = localDayKey(at);
    totals.set(key, (totals.get(key) || 0) + n);
  }
  const out: DatasetQualityDay[] = [];
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  for (let i = days; i >= 0; i -= 1) {
    const day = new Date(end.getFullYear(), end.getMonth(), end.getDate() - i);
    const key = localDayKey(day);
    out.push({
      date: key,
      label: day.toLocaleDateString(undefined, { month: "short", day: "numeric" }),
      count: totals.get(key) || 0,
    });
  }
  return out;
}

function parseTime(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? null : at;
}

function timeAgo(at: Date, now: Date): string {
  const minutes = Math.round((now.getTime() - at.getTime()) / 60000);
  if (minutes < 60) return "in the past hour";
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;
  const days = Math.round(hours / 24);
  return `${days} ${days === 1 ? "day" : "days"} ago`;
}

export function describeLastReceived(iso: string | null | undefined, now: Date = new Date()): string | null {
  const at = parseTime(iso);
  return at ? `Last received ${timeAgo(at, now)}` : null;
}

export const MATCH_QUALITY_WINDOW_HOURS = 48;
export const MATCH_QUALITY_HELP_URL = "https://www.facebook.com/business/help/765081237991954";

export function receivedBeforeScoreWindow(iso: string | null | undefined, now: Date = new Date()): boolean {
  const at = parseTime(iso);
  if (!at) return false;
  return now.getTime() - at.getTime() >= (MATCH_QUALITY_WINDOW_HOURS + 1) * 3600000;
}

export function missingScoreMessage(event: DatasetQualityEvent, now: Date = new Date()): string {
  const name = event.event_name;
  const base = `Meta did not return a match quality score or customer details for ${name}.`;
  const at = parseTime(event.last_received_at);
  if (!at || !receivedBeforeScoreWindow(event.last_received_at, now)) return base;
  return `${base} Meta uses the last ${MATCH_QUALITY_WINDOW_HOURS} hours of events to calculate scores, and it last received ${name} ${timeAgo(at, now)}.`;
}
